import styled, {css, keyframes} from "styled-components";
import {useMessages} from "i18n";
import {fontFamilies} from "theme/tokens";
import {LaneTone} from "./howItWorks";

/**
 * The two animated mini-stages that visualise each transfer mode — the P2P packet travelling a
 * dashed wire and the hosted upload/download dots hopping through the server (design `.hm-stage`,
 * ticket #45). Extracted from {@link HowItWorksDialog} into this shared module (ui#136) so the
 * features dialog, the `/p2p` page and the `/client-server` page all render the SAME stage from one
 * source of truth.
 *
 * Dumb + SSG-safe: pure token-styled markup, labels from `theme.tokens` copy (`useMessages`), no
 * window/document at module scope. Both stages are decorative (`aria-hidden`) and every animation
 * respects `prefers-reduced-motion`.
 */

/** Resolve a lane tone to its accent color / faint tint from `theme.tokens`. Shared with the dialog's
 *  other toned atoms (badges, checks, the glance table), so it lives here as the single definition. */
export const toneColor = (tone: LaneTone) => css`
	${({theme}) => (tone === "blue" ? theme.tokens.blue : theme.tokens.teal)}
`;

export const toneTint = (tone: LaneTone) => css`
	${({theme}) => (tone === "blue" ? theme.tokens.accentTint : theme.tokens.signalTint)}
`;

/**
 * The WCAG-AA-accessible TEXT companion of {@link toneColor}: the vivid accent fails contrast as the
 * small mono stage-tag label (teal on white/tint in light, blue on tint in dark), so tag text reads
 * this ink (segInk/signalInk) while decorative fills keep the vivid accent (ui#302).
 */
export const toneInk = (tone: LaneTone) => css`
	${({theme}) => (tone === "blue" ? theme.tokens.segInk : theme.tokens.signalInk)}
`;

/** Design `.hm-stage`. */
const Stage = styled.div`
	position: relative;
	height: 194px;
	border-radius: 16px;
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.fieldLine};
	overflow: hidden;
	margin-bottom: 18px;
`;

/** Design `.hm-tag` / `.hm-link` — the mono pill at the top of a stage. */
const StageTag = styled.span<{$tone: LaneTone}>`
	position: absolute;
	top: 14px;
	left: 50%;
	transform: translateX(-50%);
	font-family: ${fontFamilies.mono};
	font-size: 9.5px;
	font-weight: 700;
	letter-spacing: 0.06em;
	color: ${({$tone}) => toneInk($tone)};
	background: ${({$tone}) => toneTint($tone)};
	padding: 5px 11px;
	border-radius: 8px;
	white-space: nowrap;
`;

/** Design `.hm-dev` — a labelled device tile. */
const Device = styled.span<{$tone: LaneTone; $x: number; $y: number}>`
	position: absolute;
	left: ${({$x}) => $x}%;
	top: ${({$y}) => $y}%;
	transform: translate(-50%, -50%);
	width: 62px;
	height: 62px;
	border-radius: 16px;
	background: ${({theme}) => theme.tokens.card};
	border: 1.5px solid ${({theme}) => theme.tokens.cardBorder};
	box-shadow: 0 4px 14px rgba(20, 22, 27, 0.1);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;
	font-family: ${fontFamilies.mono};
	font-size: 9.5px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink2};

	&::before {
		content: "";
		width: 26px;
		height: 19px;
		border-radius: 5px;
		opacity: 0.9;
		background: ${({$tone}) => toneColor($tone)};
	}
`;

/** Design `.hm-wire` — the dashed P2P link. */
const Wire = styled.span`
	position: absolute;
	left: 24%;
	right: 24%;
	top: 50%;
	height: 2.5px;
	transform: translateY(-50%);
	background: repeating-linear-gradient(90deg, ${({theme}) => theme.tokens.blue} 0 6px, transparent 6px 13px);
	opacity: 0.5;
`;

const p2pPacket = keyframes`
	0%, 8% { transform: translateX(22%); }
	48%, 56% { transform: translateX(78%); }
	100% { transform: translateX(22%); }
`;

/**
 * Compositor-friendly travel rail for {@link Packet} (ui#156): the keyframes used to animate the
 * dot's `left`, forcing a layout pass per frame on an infinite animation. The rail spans the whole
 * stage, so `translateX(N%)` of its OWN width equals the old `left: N%` of the stage — same path,
 * same 4.4s ease-in-out timing, but transform-only. The base transform is the old `left: 22%` rest
 * position, which is where the packet stays under reduced motion.
 */
const PacketRail = styled.span`
	position: absolute;
	inset: 0;
	transform: translateX(22%);
	animation: ${p2pPacket} 4.4s ease-in-out infinite;
	will-change: transform;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/** Design `.hm-pk` — the packet travelling the wire, riding {@link PacketRail}'s left edge. */
const Packet = styled.span`
	position: absolute;
	top: 50%;
	left: 0;
	width: 15px;
	height: 15px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.blue};
	box-shadow: 0 0 0 6px ${({theme}) => theme.tokens.accentTint};
	transform: translate(-50%, -50%);
`;

/** Design `.hm-srvbox`. */
const ServerBox = styled.span`
	position: absolute;
	left: 50%;
	top: 35%;
	transform: translate(-50%, -50%);
	width: 120px;
	height: 50px;
	border-radius: 14px;
	background: ${({theme}) => theme.tokens.card};
	border: 1.5px solid ${({theme}) => theme.tokens.cardBorder};
	box-shadow: 0 4px 14px rgba(20, 22, 27, 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	font-family: ${fontFamilies.mono};
	font-size: 9px;
	font-weight: 700;
	letter-spacing: 0.06em;
	/* Accessible teal ink: the vivid teal fails AA as this 9px label on the light card (ui#302). */
	color: ${({theme}) => theme.tokens.signalInk};
`;

/** Design `.hm-conn` — dashed uplink/downlink lines. */
const ConnSvg = styled.svg`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	color: ${({theme}) => theme.tokens.teal};
	pointer-events: none;

	line {
		stroke: currentColor;
		stroke-width: 1.4;
		stroke-dasharray: 3 4;
		opacity: 0.4;
		vector-effect: non-scaling-stroke;
	}
`;

const hostedUp = keyframes`
	0% { transform: translate(16%, 80%); opacity: 0; }
	9% { opacity: 1; }
	44% { transform: translate(50%, 35%); opacity: 1; }
	52%, 100% { transform: translate(50%, 35%); opacity: 0; }
`;

const hostedDown = keyframes`
	0%, 44% { transform: translate(50%, 35%); opacity: 0; }
	53% { transform: translate(50%, 35%); opacity: 1; }
	90% { transform: translate(84%, 80%); opacity: 1; }
	100% { transform: translate(84%, 80%); opacity: 0; }
`;

/**
 * Compositor-friendly travel rail for one {@link HostedDot} (ui#156): the keyframes used to move
 * the dots via `left`/`top` (layout per frame, infinite). The rail spans the whole stage, so a
 * `translate(X%, Y%)` of its OWN size equals the old `left: X%; top: Y%` of the stage — identical
 * waypoints, opacity ramps and 4.8s ease-in-out timing, but transform/opacity-only. Reduced motion
 * keeps the old end state: no animation, dot invisible (base `opacity: 0`).
 */
const HostedDotRail = styled.span<{$leg: "up" | "down"}>`
	position: absolute;
	inset: 0;
	opacity: 0;
	animation: ${({$leg}) => ($leg === "up" ? hostedUp : hostedDown)} 4.8s ease-in-out infinite;
	will-change: transform, opacity;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/** Design `.hm-updot` / `.hm-dndot` — the upload/download packets, riding {@link HostedDotRail}. */
const HostedDot = styled.span`
	position: absolute;
	left: 0;
	top: 0;
	width: 13px;
	height: 13px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.teal};
	box-shadow: 0 0 0 5px ${({theme}) => theme.tokens.signalTint};
	transform: translate(-50%, -50%);
`;

/** The P2P lane's animated mini-stage (design `.hm-stage.hm-p2p`). */
export function P2pStage() {
	const {dialog} = useMessages().features;
	return (
		<Stage aria-hidden="true">
			<StageTag $tone="blue">{dialog.stageP2p}</StageTag>
			<Device $tone="blue" $x={16} $y={50}>{dialog.you}</Device>
			<Wire/>
			<Device $tone="blue" $x={84} $y={50}>{dialog.them}</Device>
			<PacketRail><Packet/></PacketRail>
		</Stage>
	);
}

/** The hosted lane's animated mini-stage (design `.hm-stage.hm-srv`). */
export function HostedStage() {
	const {dialog} = useMessages().features;
	return (
		<Stage aria-hidden="true">
			<ConnSvg viewBox="0 0 100 49" preserveAspectRatio="none" aria-hidden="true">
				<line x1="16" y1="39" x2="50" y2="17"/>
				<line x1="50" y1="17" x2="84" y2="39"/>
			</ConnSvg>
			<StageTag $tone="teal">{dialog.stageHosted}</StageTag>
			<ServerBox>{dialog.server}</ServerBox>
			<Device $tone="teal" $x={16} $y={80}>{dialog.you}</Device>
			<Device $tone="teal" $x={84} $y={80}>{dialog.them}</Device>
			<HostedDotRail $leg="up"><HostedDot/></HostedDotRail>
			<HostedDotRail $leg="down"><HostedDot/></HostedDotRail>
		</Stage>
	);
}
